import React, { useRef, useState, useEffect, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf/dist/esm/entry.webpack";
import styled from "styled-components";

import "react-pdf/dist/esm/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
const WHEEL_LOCK_MS = 450;


export default function ManualPdfViewer({
    fileUrl,
    numPages,
    pageNumber,
    setNumPages,
    setPageNumber
}) {
    const wheelLockedRef = useRef(false);
    const viewerRef = useRef(null);

    const [pageWidth, setPageWidth] = useState(900);
    const [pageOriginalSize, setPageOriginalSize] = useState({ width: 1, height: 1 });

    useEffect(() => {
        setPageNumber(1);
    }, [fileUrl]);

    const goToPrevPage = useCallback(() => {
        setPageNumber((prev) => Math.max(prev - 1, 1));
    }, [setPageNumber]);

    const goToNextPage = useCallback(() => {
        setPageNumber((prev) => Math.min(prev + 1, numPages));
    }, [setPageNumber, numPages]);

    useEffect(() => {
        const node = viewerRef.current;

        if (!node) {
            return undefined;
        }

        const handleNativeWheel = (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (wheelLockedRef.current || !numPages) {
                return;
            }

            if (Math.abs(e.deltaY) < 8) {
                return;
            }

            wheelLockedRef.current = true;

            if (e.deltaY > 0) {
                goToNextPage();
            } else {
                goToPrevPage();
            }

            window.setTimeout(() => {
                wheelLockedRef.current = false;
            }, WHEEL_LOCK_MS);
        };

        node.addEventListener("wheel", handleNativeWheel, { passive: false });

        return () => {
            node.removeEventListener("wheel", handleNativeWheel, { passive: false });
        };
    }, [numPages, goToNextPage, goToPrevPage]);

    const updatePageWidth = () => {
        if (!viewerRef.current || !pageOriginalSize.width || !pageOriginalSize.height) {
            return;
        }

        const containerWidth = viewerRef.current.clientWidth - 32;
        const containerHeight = viewerRef.current.clientHeight - 32;

        const widthScale = containerWidth / pageOriginalSize.width;
        const heightScale = containerHeight / pageOriginalSize.height;

        const scale = Math.min(widthScale, heightScale);

        setPageWidth(Math.floor(pageOriginalSize.width * scale));
    };

    useEffect(() => {
        updatePageWidth();

        window.addEventListener("resize", updatePageWidth);
        return () => {
            window.removeEventListener("resize", updatePageWidth);
        };
    }, [pageOriginalSize]);


    const handleDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setPageNumber(1);
    };

    const handlePageLoadSuccess = (page) => {
        const viewport = page.getViewport({ scale: 1 });
        setPageOriginalSize({
            width: viewport.width,
            height: viewport.height,
        });
    };

    const handleKeyDown = (e) => {
        if (e.key === "ArrowDown" || e.key === "PageDown") {
            e.preventDefault();
            goToNextPage();
        }

        if (e.key === "ArrowUp" || e.key === "PageUp") {
            e.preventDefault();
            goToPrevPage();
        }
    };


    return (
        <PDFContainer
            ref={viewerRef}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            onContextMenu={(e) => e.preventDefault()}
        >
            <Document
                file={fileUrl}
                onLoadSuccess={handleDocumentLoadSuccess}
                options={{ isEvalSupported: false }}
                loading={<div>Loading PDF…</div>}
                error={<div>Failed to load PDF.</div>}
                noData={<div>No PDF file specified.</div>}
            >
                <Page
                    pageNumber={pageNumber}
                    width={pageWidth}
                    onLoadSuccess={handlePageLoadSuccess}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                />
            </Document>
        </PDFContainer>
    );
};


const PDFContainer = styled.div`
    && {
        width: 100%;
        height: 100%;
        min-width: 0;
        min-height: 0;
        overflow: hidden;
        overscroll-behavior: contain;
        display: flex;
        align-items: center;
        justify-content: center;
        outline: none;
        box-sizing: border-box;
    }
`;